import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "../../components/Sidebar";
import { api } from "../../lib/api";
import { ClipboardCheck, Search, ChevronUp, ChevronDown } from "lucide-react";

const scoreClass = (s) => {
  const v = Number(s ?? 0);
  if (v >= 75) return "text-emerald-700 bg-emerald-50";
  if (v >= 50) return "text-amber-700 bg-amber-50";
  return "text-rose-700 bg-rose-50";
};

const SortIcon = ({ sort, k }) => sort.key !== k ? null : (sort.dir === "asc" ? <ChevronUp size={12} className="inline" /> : <ChevronDown size={12} className="inline" />);

export default function VRQuizResults() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ date: "", username: "", room: "" });
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && params.append(k, v));
    api.get(`/admin/vr-quiz-results?${params}`).then((r) => setRows(r.data.items || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const x = a[sort.key] ?? "", y = b[sort.key] ?? "";
      if (typeof x === "number" && typeof y === "number") return sort.dir === "asc" ? x - y : y - x;
      return sort.dir === "asc" ? String(x).localeCompare(String(y)) : String(y).localeCompare(String(x));
    });
    return arr;
  }, [rows, sort]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  return (
    <AppShell role="admin">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600"><ClipboardCheck size={20} /></div>
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">VR Quiz Results</h1>
          <p className="mt-1 text-slate-500">Quiz submissions posted by the Unity VR headset.</p>
        </div>
      </div>

      <div className="mt-6 grid gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-card sm:grid-cols-2 lg:grid-cols-4">
        <FilterInput placeholder="Date (YYYY-MM-DD)" value={f.date} onChange={(v) => setF({ ...f, date: v })} testId="filter-date" />
        <FilterInput placeholder="Username" value={f.username} onChange={(v) => setF({ ...f, username: v })} testId="filter-username" />
        <FilterInput placeholder="Room" value={f.room} onChange={(v) => setF({ ...f, room: v })} testId="filter-room" />
        <button
          onClick={load}
          data-testid="vr-quiz-apply-btn"
          className="rounded-xl bg-indigo-600 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700"
        >Apply</button>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-4 shadow-card">
        <div className="mb-2 text-sm font-semibold text-slate-700">Total: {sorted.length}</div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm" data-testid="vr-quiz-table">
            <thead>
              <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {[
                  ["date", "Date"], ["time", "Time"], ["username", "Username"], ["full_name", "Name"],
                  ["room_name", "Room"],
                  ["total_questions", "Total Q"], ["questions_answered", "Answered"], ["correct_answers", "Correct"],
                  ["score", "Score"],
                ].map(([k, label]) => (
                  <th key={k} onClick={() => toggleSort(k)} className="cursor-pointer select-none py-3 pr-4 hover:text-slate-900">
                    {label} <SortIcon sort={sort} k={k} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="py-6 text-center text-slate-500">Loading…</td></tr>}
              {!loading && sorted.length === 0 && (
                <tr><td colSpan={9} className="py-8 text-center text-slate-500">No VR quiz results yet.</td></tr>
              )}
              {!loading && sorted.map((r) => (
                <tr key={r.id} className="border-b border-slate-50">
                  <td className="py-3 pr-4 text-slate-700">{r.date || "—"}</td>
                  <td className="py-3 pr-4 text-slate-700">{r.time || "—"}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-slate-800">{r.username || "—"}</td>
                  <td className="py-3 pr-4 text-slate-700">{r.full_name || "—"}</td>
                  <td className="py-3 pr-4 text-slate-700">{r.room_name || "—"}</td>
                  <td className="py-3 pr-4 text-slate-700">{r.total_questions ?? "—"}</td>
                  <td className="py-3 pr-4 text-slate-700">{r.questions_answered ?? "—"}</td>
                  <td className="py-3 pr-4 text-slate-700">{r.correct_answers ?? "—"}</td>
                  <td className="py-3 pr-4">
                    <span className={`rounded-md px-2 py-1 text-xs font-bold ${scoreClass(r.score)}`}>{r.score ?? "—"}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}

const FilterInput = ({ placeholder, value, onChange, testId }) => (
  <div className="relative">
    <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
    />
  </div>
);
